import type {
  IPackageChange,
  IPackageChecklistAnswer,
  IPackageConflict,
  IPackagePageMerge,
  IPackageUpgradeDeclaration,
  PackageComponentNameType,
  PackageLabelsType,
  PackageRoleWordsType,
} from '@featit/shared';
import { ApiService } from './ApiService';
import { workAreaApis } from '@/data-sources/WorkAreaApiRegistry';
import { expectList, expectRecord } from './answerShape';

/**
 * T-0800, AGM-56: Settings → Packages' reads and its write — the packages the server ships with the
 * version installed here (`GET packages/catalogue`), and installing or upgrading one by its key
 * (`POST packages/:key/install`), the installer `featit package install` reaches with a file.
 */
export type PackageCatalogueActionType =
  | 'install'
  | 'upgrade'
  | 'current'
  | 'newer-installed'
  | 'none';

export interface IPackageCatalogueEntry {
  key: string;
  title: string;
  /** F-0621: the title's words in other languages, for a package not installed yet. */
  titleLabels?: PackageLabelsType | null;
  description: string | null;
  /** I-2147: the description's words in other languages, for a package not installed yet. */
  descriptionLabels?: PackageLabelsType | null;
  shippedVersion: string | null;
  /** F-0794 S8a: a package published in an organisation this work area may install from. */
  published?: { version: string; publisher: string };
  /** F-0794 S8e: on a published package installed here, whether it upgrades itself. */
  autoUpgrade?: IPackageAutoUpgrade;
  installedVersion: string | null;
  installedAt: string | null;
  action: PackageCatalogueActionType;
}

/**
 * F-0794 S8e (§9.3): a published package's automatic upgrade here: off unless a manager switched it
 * on, and why the last automatic upgrade stopped, with each component it refused.
 */
export interface IPackageAutoUpgrade {
  key: string;
  enabled: boolean;
  failure: string | null;
  failedVersion: string | null;
  failureRefusals: IPackageRefusal[];
  failedAt: string | null;
}

export interface IPackageInstallLine {
  kind: string;
  key: string;
  name: string;
  action: 'create' | 'exists' | 'clash' | 'not-applied' | 'update' | 'refused';
  note?: string;
  /** AGM-56: what `note` says, as codes the screen words in its reader's language. */
  changes?: IPackageChange[];
  /** §23.2 (3): deleted here since the install; naming it in `recreate` makes it again. */
  gone?: boolean;
  /** F-0794 S8g (§9.3a): on a refused line of an upgrade's preview, how it may be answered. */
  conflict?: IPackageConflict;
}

export interface IPackageInstallResult {
  package: { key: string; version: string; title: string };
  dryRun: boolean;
  alreadyInstalled: boolean;
  lines: IPackageInstallLine[];
  created: number;
  changed: number;
  notApplied: string[];
  summary: string;
}

/**
 * `dryRun`: what an install would make and change, with nothing written (AGM-56's preview); `seed`:
 * with the package's example content (AGM-23's second part).
 */
export interface IPackageInstallOptions {
  dryRun?: boolean;
  seed?: boolean;
  /** §23.2 (3): the deleted records the reader ticked, each by kind and the package's key. */
  recreate?: Array<{ kind: string; key: string }>;
  /**
   * F-0794 S8g (§9.3a): a preview that lists each refused component as a line to answer, instead of
   * refusing; and the answers, each as `kind:key` — keep it, take the package's, or merge a page.
   */
  answerRefusals?: boolean;
  keep?: string[];
  override?: string[];
  merge?: Record<string, IPackagePageMerge>;
}

/** One record a refused install names, and why (AGM-52). */
export interface IPackageRefusal {
  kind: string;
  key: string;
  name: string;
  reason: string;
  /** AGM-56: the same, as codes the screen words in its reader's language. */
  reasons?: IPackageChange[];
}

/** F-0794 S8f (PIO-34f): the words a publish of this work area is asked with. */
export interface IPackagePublishInput {
  key: string;
  version: string;
  title?: string;
  description?: string;
}

/** F-0794 S8d (PIO-21e): what a publish would store, answered with nothing written. */
export interface IPackagePublishPreview {
  key: string;
  version: string;
  /** The version the declarations are computed from; null for a first version. */
  previous: string | null;
  declarations: IPackageUpgradeDeclaration[];
  /** Each component that changed in a way no declaration says, as an upgrade's refusal names it. */
  notComputed: PackageComponentNameType[];
  digest: string;
}

/** F-0794 S8a (PIO-20): a stored version; `created` false when this content was published before. */
export interface IPublishedPackageVersion {
  key: string;
  version: string;
  digest: string;
  created: boolean;
}

const publishBody = (input: IPackagePublishInput) => ({
  key: input.key,
  version: input.version,
  ...(input.title ? { title: input.title } : {}),
  ...(input.description ? { description: input.description } : {}),
});

class PackagesApiService extends ApiService {
  async catalogue(workAreaId: string): Promise<IPackageCatalogueEntry[]> {
    return expectList<IPackageCatalogueEntry>(
      await this.get(`/work-areas/${workAreaId}/packages/catalogue`),
      'packages',
    );
  }

  async install(
    workAreaId: string,
    packageKey: string,
    options: IPackageInstallOptions = {},
  ): Promise<IPackageInstallResult> {
    return expectRecord<IPackageInstallResult>(
      await this.post(
        `/work-areas/${workAreaId}/packages/${encodeURIComponent(packageKey)}/install`,
        {
          ...(options.dryRun ? { dryRun: true } : {}),
          ...(options.seed ? { seed: true } : {}),
          ...(options.recreate && options.recreate.length > 0
            ? { recreate: options.recreate }
            : {}),
          ...(options.answerRefusals ? { answerRefusals: true } : {}),
          ...(options.keep?.length ? { keep: options.keep } : {}),
          ...(options.override?.length ? { override: options.override } : {}),
          ...(options.merge && Object.keys(options.merge).length > 0
            ? { merge: options.merge }
            : {}),
        },
      ),
      'package install',
      ['package', 'lines'],
    );
  }

  /** F-0794 S8f (PIO-34f): what publishing this work area would store, with nothing published. */
  async previewPublish(
    workAreaId: string,
    input: IPackagePublishInput,
  ): Promise<IPackagePublishPreview> {
    return expectRecord<IPackagePublishPreview>(
      await this.post(`/work-areas/${workAreaId}/packages/publish/preview`, publishBody(input)),
      'package publish preview',
      ['key', 'version', 'declarations'],
    );
  }

  /** F-0794 S8f (PIO-34f): publishes this work area as `version` of `key`. */
  async publish(
    workAreaId: string,
    input: IPackagePublishInput,
  ): Promise<IPublishedPackageVersion> {
    return expectRecord<IPublishedPackageVersion>(
      await this.post(`/work-areas/${workAreaId}/packages/publish`, publishBody(input)),
      'package publish',
      ['key', 'version', 'created'],
    );
  }

  /** F-0794 S8f (PIO-34a): switches a published package's automatic upgrade here on or off. */
  async setAutoUpgrade(
    workAreaId: string,
    packageKey: string,
    enabled: boolean,
  ): Promise<IPackageAutoUpgrade> {
    return expectRecord<IPackageAutoUpgrade>(
      await this.put(
        `/work-areas/${workAreaId}/packages/${encodeURIComponent(packageKey)}/auto-upgrade`,
        { enabled },
      ),
      'package automatic upgrade',
      ['key', 'enabled'],
    );
  }

  /** UXA-7: the checklist each installed package left on the home, ticked from the work area. */
  async checklists(workAreaId: string): Promise<IPackageChecklistAnswer[]> {
    return expectList<IPackageChecklistAnswer>(
      await this.get(`/work-areas/${workAreaId}/packages/checklist`),
      'package checklists',
    );
  }

  /** I-1977: the German (and any other language's) words of the names installed here, by their English. */
  async labels(workAreaId: string): Promise<PackageLabelsType> {
    return expectRecord<PackageLabelsType>(
      await this.get(`/work-areas/${workAreaId}/packages/labels`),
      'package labels',
      [],
    );
  }

  /** I-2496 (AGM-77): the word the packages installed here give each role, by role key. */
  async roleWords(workAreaId: string): Promise<PackageRoleWordsType> {
    return expectRecord<PackageRoleWordsType>(
      await this.get(`/work-areas/${workAreaId}/packages/role-words`),
      'package role words',
      [],
    );
  }

  /** UXA-7: a manager dismisses one; the answer is every checklist after it. */
  async dismissChecklist(
    workAreaId: string,
    packageKey: string,
  ): Promise<IPackageChecklistAnswer[]> {
    return expectList<IPackageChecklistAnswer>(
      await this.post(
        `/work-areas/${workAreaId}/packages/${encodeURIComponent(packageKey)}/checklist/dismiss`,
        {},
      ),
      'package checklists',
    );
  }
}

export const packagesApi = new PackagesApiService();

/** The packages API for the connection that holds `workAreaId` (I-1120). */
export function packagesApiFor(workAreaId: string): PackagesApiService {
  return workAreaApis.api(
    workAreaId,
    'packagesApi',
    (clients) => new PackagesApiService(clients.transport),
    packagesApi,
  );
}

export function fetchPackageCatalogue(workAreaId: string): Promise<IPackageCatalogueEntry[]> {
  return packagesApiFor(workAreaId).catalogue(workAreaId);
}

export function installShippedPackage(
  workAreaId: string,
  packageKey: string,
  options: IPackageInstallOptions = {},
): Promise<IPackageInstallResult> {
  return packagesApiFor(workAreaId).install(workAreaId, packageKey, options);
}

export function previewPackagePublish(
  workAreaId: string,
  input: IPackagePublishInput,
): Promise<IPackagePublishPreview> {
  return packagesApiFor(workAreaId).previewPublish(workAreaId, input);
}

export function publishPackage(
  workAreaId: string,
  input: IPackagePublishInput,
): Promise<IPublishedPackageVersion> {
  return packagesApiFor(workAreaId).publish(workAreaId, input);
}

export function setPackageAutoUpgrade(
  workAreaId: string,
  packageKey: string,
  enabled: boolean,
): Promise<IPackageAutoUpgrade> {
  return packagesApiFor(workAreaId).setAutoUpgrade(workAreaId, packageKey, enabled);
}

export function fetchPackageChecklists(workAreaId: string): Promise<IPackageChecklistAnswer[]> {
  return packagesApiFor(workAreaId).checklists(workAreaId);
}

export function fetchPackageLabels(workAreaId: string): Promise<PackageLabelsType> {
  return packagesApiFor(workAreaId).labels(workAreaId);
}

export function fetchPackageRoleWords(workAreaId: string): Promise<PackageRoleWordsType> {
  return packagesApiFor(workAreaId).roleWords(workAreaId);
}

export function dismissPackageChecklist(
  workAreaId: string,
  packageKey: string,
): Promise<IPackageChecklistAnswer[]> {
  return packagesApiFor(workAreaId).dismissChecklist(workAreaId, packageKey);
}

/**
 * The records a refused install names, from its 400 (AGM-52, AGM-56), or null for any other failure:
 * the server answers `refusals` beside its sentence, and that nothing was written.
 */
export function packageRefusalsOf(error: unknown): IPackageRefusal[] | null {
  const response = (error as { response?: { status?: unknown; data?: unknown } } | null)?.response;
  if (response?.status !== 400) return null;
  const refusals = (response.data as { refusals?: unknown } | null)?.refusals;
  if (!Array.isArray(refusals) || refusals.length === 0) return null;
  return refusals.filter(
    (entry): entry is IPackageRefusal =>
      typeof entry === 'object' &&
      entry !== null &&
      typeof (entry as IPackageRefusal).name === 'string' &&
      typeof (entry as IPackageRefusal).reason === 'string',
  );
}
